import { inject, Injectable } from '@angular/core';
import { NgxImageCompressService } from 'ngx-image-compress';
import { FIREBASE_STORAGE } from '../../fireauth-lib.config';
import { from, forkJoin, Observable, throwError, of } from 'rxjs';
import { mergeMap, map, toArray, catchError, switchMap, tap } from 'rxjs/operators';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from '@angular/fire/storage';
import { UserMedia } from '../models/user-media';
import { UserMediaService, CreateUserMediaDto, BatchCreateUserMediaDto } from 'cms-post-lib';

interface UploadResult {
  fileName: string;
  smallUrl: string;
  largeUrl: string;
}



@Injectable({ providedIn: 'root' })
export class MediaService {
  storage = inject(FIREBASE_STORAGE);
  compressor = inject(NgxImageCompressService);
  userMediaService = inject(UserMediaService);

  constructor() { }

  /**
   * Resizes and compresses an image before uploading.
   * @param file The original image file.
   * @param maxSizePx The maximum width/height (whichever is larger).
   * @param quality Compression quality (0 to 1).
   * @returns A Blob of the resized image.
   */
  // Note: The quality parameter is a percentage (0-100) for the compressor.
  private async resizeImage(file: File, maxSizePx: number, quality = 0.8): Promise<Blob> {
    // 1) read file as Data URL
    const dataUrl = await this.fileToDataUrl(file);

    // 2) compute scale ratio
    const img = new Image();
    img.src = dataUrl;
    await img.decode();
    const ratio = Math.min(1, maxSizePx / Math.max(img.width, img.height)) * 100;

    // 3) use the injected compressor
    const compressedDataUrl = await this.compressor.compressFile(
      dataUrl,
      -1,            // ignore EXIF orientation
      ratio,         // percent scale
      quality * 100  // quality percent
    );

    // 4) convert back to Blob
    return this.dataURLtoBlob(compressedDataUrl);
  }

  /**
 * Resizes and compresses an image to fit within given width and height,
 * preserving aspect ratio.
 * @param file The original image file.
 * @param maxWidth The maximum width to scale down to.
 * @param maxHeight The maximum height to scale down to.
 * @param quality Compression quality (0 to 1).
 * @returns A Blob of the resized image.
 */
  private async resizeWithWidthAndHeight(
    file: File,
    maxWidth: number,
    maxHeight: number,
    quality = 0.8
  ): Promise<Blob> {
    // 1) read file as Data URL
    const dataUrl = await this.fileToDataUrl(file);

    // 2) decode image to get dimensions
    const img = new Image();
    img.src = dataUrl;
    await img.decode();

    // 3) calculate scale percentage based on both width and height
    const ratioX = maxWidth / img.width;
    const ratioY = maxHeight / img.height;
    const scalePercent = Math.min(1, ratioX, ratioY) * 100;

    // 4) compress using the injected compressor
    const compressedDataUrl = await this.compressor.compressFile(
      dataUrl,
      -1,              // ignore EXIF orientation
      scalePercent,    // percent scale
      quality * 100    // quality percent
    );

    // 5) convert back to Blob
    return this.dataURLtoBlob(compressedDataUrl);
  }
  

  /**
   * Converts a File to a Data URL.
   * @param file The file to convert.
   * @returns A promise that resolves to the Data URL.
   */
  // Note: This is a utility function to read the file as a Data URL.
  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result as string);
      reader.onerror = e => rej(e);
      reader.readAsDataURL(file);
    });
  }
  /**
   * Converts a Data URL to a Blob.
   * @param dataUrl The Data URL to convert.
   * @returns A Blob representing the image.
   */
  // Note: This is a utility function to convert the Data URL back to a Blob.
  private dataURLtoBlob(dataUrl: string): Blob {
    const [meta, base64] = dataUrl.split(',');
    const mime = meta.match(/:(.*?);/)![1];
    const binary = atob(base64);
    const len = binary.length;
    const buf = new Uint8Array(len);
    for (let i = 0; i < len; i++) buf[i] = binary.charCodeAt(i);
    return new Blob([buf], { type: mime });
  }

  /**
   * Uploads multiple images to Firebase Storage and Firestore.
   * @param files The array of image files to upload.
   * @param userId The user ID for the storage path.
   * @param orgCode The organization code for the storage path.
   * @returns An observable of the upload results.
   */
  // Note: This method uses RxJS to handle multiple uploads concurrently.
  // It uses mergeMap to limit concurrency to 3 uploads at a time.
  // The toArray operator collects all results into an array.
  uploadImages(
    files: File[],
    userId: string,
    orgCode: string
  ): Observable<UploadResult[]> {
    return from(files).pipe(
      mergeMap(file => this.uploadSingle(file, userId, orgCode), /* concurrency */ 3),
      toArray()
    );
  }

  /**
   * Uploads multiple images using batch API to avoid rate limiting.
   * This method processes all files (resize & upload to Firebase Storage) first,
   * then creates all media documents in a single API call.
   * 
   * @param files The array of image files to upload.
   * @param userId The user ID for the storage path.
   * @param orgCode The organization code for the storage path.
   * @returns An observable of the upload results.
   * 
   * @remarks
   * This method is preferred over uploadImages() when uploading multiple files
   * as it reduces API calls and avoids rate limiting (HTTP 429 errors).
   * 
   * @example
   * ```typescript
   * this.mediaService.uploadBatch(files, userId, orgCode).subscribe({
   *   next: (results) => console.log('All uploads completed:', results),
   *   error: (err) => console.error('Upload failed:', err)
   * });
   * ```
   */
  uploadBatch(
    files: File[],
    userId: string,
    orgCode: string
  ): Observable<UploadResult[]> {
    // Step 1: Process all files (resize and upload to Firebase Storage) concurrently
    return from(files).pipe(
      mergeMap(file => from(this.processFileForBatch(file, userId, orgCode)), /* concurrency */ 3),
      toArray(),
      // Step 2: Create all media documents in a single batch API call
      switchMap(processedFiles => {
        const mediaDtos: CreateUserMediaDto[] = processedFiles.map((processed) => ({
          fileName: processed.fileName,
          orgId: orgCode,
          fileSize: processed.fileSize,
          urls: {
            large: processed.largeUrl,
            medium: processed.mediumUrl,
            small: processed.smallUrl,
            mobile: processed.mobileUrl
          }
        }));

        const batchDto: BatchCreateUserMediaDto = {
          medias: mediaDtos
        };

        // Make single batch API call
        return this.userMediaService.batchCreateMedias(batchDto).pipe(
          map(response => {
            // Transform response to match UploadResult format
            return processedFiles.map(processed => ({
              fileName: processed.fileName,
              smallUrl: processed.smallUrl,
              largeUrl: processed.largeUrl
            }));
          })
        );
      }),
      catchError(error => {
        console.error('Batch upload failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Processes a single file for batch upload (resize and upload to Firebase Storage only).
   * Does NOT create media document - that's done in batch.
   * Uses RxJS patterns to maintain Angular injection context for Firebase calls.
   * 
   * @param file The image file to process.
   * @param userId The user ID for the storage path.
   * @param orgCode The organization code for the storage path.
   * @returns A promise with the processed file data including all URLs.
   */
  private processFileForBatch(
    file: File,
    userId: string,
    orgCode: string
  ): Observable<{
    fileName: string;
    fileSize: number;
    largeUrl: string;
    mediumUrl: string;
    smallUrl: string;
    mobileUrl: string;
  }> {
    const baseName = `${userId}_${orgCode}_${Date.now()}_${file.name}`;

    // 1) Resize to all required sizes
    return forkJoin({
      large: from(this.resizeImage(file, 800)),
      medium: from(this.resizeImage(file, 600)),
      small: from(this.resizeImage(file, 200)),
      mobile: from(this.resizeWithWidthAndHeight(file, 800, 400)),
    }).pipe(
      switchMap(({ large, medium, small, mobile }) => {
        // 2) Upload all sizes to Firebase Storage (in injection context)
        const largePath = `usermedias/${userId}/${orgCode}/${baseName}_lg.jpg`;
        const mediumPath = `usermedias/${userId}/${orgCode}/${baseName}_md.jpg`;
        const smallPath = `usermedias/${userId}/${orgCode}/${baseName}_sm.jpg`;
        const mobilePath = `usermedias/${userId}/${orgCode}/${baseName}_mobile.jpg`;

        return forkJoin({
          largeSnap: from(uploadBytes(ref(this.storage, largePath), large, { cacheControl: 'public,max-age=31536000' })),
          mediumSnap: from(uploadBytes(ref(this.storage, mediumPath), medium, { cacheControl: 'public,max-age=31536000' })),
          smallSnap: from(uploadBytes(ref(this.storage, smallPath), small, { cacheControl: 'public,max-age=31536000' })),
          mobileSnap: from(uploadBytes(ref(this.storage, mobilePath), mobile, { cacheControl: 'public,max-age=31536000' }))
        });
      }),
      switchMap(({ largeSnap, mediumSnap, smallSnap, mobileSnap }) => {
        // 3) Get download URLs (in injection context)
        return forkJoin({
          largeUrl: from(getDownloadURL(largeSnap.ref)),
          mediumUrl: from(getDownloadURL(mediumSnap.ref)),
          smallUrl: from(getDownloadURL(smallSnap.ref)),
          mobileUrl: from(getDownloadURL(mobileSnap.ref))
        });
      }),
      map(({ largeUrl, mediumUrl, smallUrl, mobileUrl }) => ({
        fileName: file.name,
        fileSize: file.size,
        largeUrl,
        mediumUrl,
        smallUrl,
        mobileUrl
      }))
    );
  }

  /**
   * Uploads a single image to Firebase Storage and Firestore.
   * @param file The image file to upload.
   * @param userId The user ID for the storage path.
   * @param orgCode The organization code for the storage path.
   * @returns An observable of the upload result.
   */
  private uploadSingle(
    file: File,
    userId: string,
    orgCode: string
  ): Observable<UploadResult> {
    const baseName = `${userId}_${orgCode}_${Date.now()}_${file.name}`;
    // 1) Resize to all sizes
    return forkJoin({
      large: from(this.resizeImage(file, 800)),
      medium: from(this.resizeImage(file, 600)),
      small: from(this.resizeImage(file, 200)),
      mobile: from(this.resizeWithWidthAndHeight(file, 800, 400)),
    }).pipe(
      switchMap(({ large, medium, small, mobile }) => {
        // 2) Upload all sizes to Firebase Storage (in injection context)
        const largePath = `usermedias/${userId}/${orgCode}/${baseName}_lg.jpg`;
        const mediumPath = `usermedias/${userId}/${orgCode}/${baseName}_md.jpg`;
        const smallPath = `usermedias/${userId}/${orgCode}/${baseName}_sm.jpg`;
        const mobilePath = `usermedias/${userId}/${orgCode}/${baseName}_mobile.jpg`;

        return forkJoin({
          largeSnap: from(uploadBytes(ref(this.storage, largePath), large, { cacheControl: 'public,max-age=31536000' })),
          mediumSnap: from(uploadBytes(ref(this.storage, mediumPath), medium, { cacheControl: 'public,max-age=31536000' })),
          smallSnap: from(uploadBytes(ref(this.storage, smallPath), small, { cacheControl: 'public,max-age=31536000' })),
          mobileSnap: from(uploadBytes(ref(this.storage, mobilePath), mobile, { cacheControl: 'public,max-age=31536000' }))
        });
      }),
      switchMap(({ largeSnap, mediumSnap, smallSnap, mobileSnap }) => {
        // 3) Get download URLs (in injection context)
        return forkJoin({
          largeUrl: from(getDownloadURL(largeSnap.ref)),
          mediumUrl: from(getDownloadURL(mediumSnap.ref)),
          smallUrl: from(getDownloadURL(smallSnap.ref)),
          mobileUrl: from(getDownloadURL(mobileSnap.ref))
        });
      }),
      switchMap(({ largeUrl, mediumUrl, smallUrl, mobileUrl }) => {
        // 4) Save to MongoDB via API
        const mediaDto: CreateUserMediaDto = {
          fileName: file.name,
          orgId: orgCode,
          fileSize: file.size,
          urls: {
            large: largeUrl,
            medium: mediumUrl,
            small: smallUrl,
            mobile: mobileUrl
          }
        };

        return this.userMediaService.createMedia(mediaDto).pipe(
          map(() => ({ 
            fileName: file.name, 
            largeUrl, 
            mediumUrl, 
            smallUrl, 
            mobileUrl 
          }))
        );
      }),
      map(res => res as UploadResult)
    );
  }


  /**
   * Fetches media documents for an organization, optionally filtering by user.
   * Uses MongoDB API to retrieve medias.
   */
  // Note: This method uses the UserMediaService API which returns paginated results.
  // We extract and transform the media items to match the expected UserMedia interface.
  // The API returns populated orgId and userId objects, we need to handle this transformation.
  getUserMedias(orgCode: string, userId?: string | null): Observable<UserMedia[]> {
    // Use getMediasByOrganization for organization-scoped query
    // Note: orgCode is being used as orgId (to be deprecated in future)
    // Using limit of 100 (server max) to comply with validation
    const apiCall = this.userMediaService.getMediasByOrganization(orgCode, { 
      page: 1, 
      limit: 100
    });
    
    return apiCall.pipe(
      map(response => {
        let medias = response.data.medias;
        
        // Filter by userId if provided
        if (userId) {
          medias = medias.filter(media => {
            // Handle both populated and unpopulated userId
            const mediaUserId = typeof media.userId === 'object' ? media.userId._id : media.userId;
            return mediaUserId === userId;
          });
        }
        
        // Transform API response to UserMedia interface
        const transformedMedias = medias.map(media => ({
          _id: media._id,
          userId: typeof media.userId === 'object' ? media.userId._id : media.userId,
          orgId: typeof media.orgId === 'object' ? media.orgId._id : media.orgId,
          orgCode: typeof media.orgId === 'object' ? media.orgId.orgCode : undefined,
          fileName: media.fileName,
          urls: media.urls,
          description: media.description || '',
          caption: media.caption || '',
          isDeleted: media.isDeleted,
          createdAt: media.createdAt,
          updatedAt: media.updatedAt,
          deletedAt: media.deletedAt
        }));
        
        return transformedMedias;
      }),
      catchError(error => {
        console.error('Error fetching user medias:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fetches paginated media documents for an organization, optionally filtering by user.
   * Returns both the media items and pagination metadata.
   */
  getUserMediasWithPagination(orgCode: string, userId?: string | null, page: number = 1, limit: number = 12): Observable<{ medias: UserMedia[], total: number }> {
    // Use getMediasByOrganization for organization-scoped query
    // Note: orgCode is being used as orgId (to be deprecated in future)
    const apiCall = this.userMediaService.getMediasByOrganization(orgCode, { 
      page, 
      limit
    });
    
    return apiCall.pipe(
      map(response => {
        let medias = response.data.medias;
        const pagination = response.data.pagination;
        
        // Filter by userId if provided
        if (userId) {
          medias = medias.filter(media => {
            // Guard against null — typeof null === 'object' in JS
            const mediaUserId = media.userId !== null && typeof media.userId === 'object' ? media.userId._id : media.userId;
            return mediaUserId === userId;
          });
        }
        
        // Transform API response to UserMedia interface
        const transformedMedias = medias.map(media => ({
          _id: media._id,
          userId: media.userId !== null && typeof media.userId === 'object' ? media.userId._id : media.userId,
          orgId: media.orgId !== null && typeof media.orgId === 'object' ? media.orgId._id : media.orgId,
          orgCode: media.orgId !== null && typeof media.orgId === 'object' ? media.orgId.orgCode : undefined,
          fileName: media.fileName,
          urls: media.urls,
          description: media.description || '',
          caption: media.caption || '',
          isDeleted: media.isDeleted,
          createdAt: media.createdAt,
          updatedAt: media.updatedAt,
          deletedAt: media.deletedAt
        }));
        
        return { medias: transformedMedias, total: pagination.total };
      }),
      catchError(error => {
        console.error('Error fetching user medias:', error);
        return throwError(() => error);
      })
    );
  }


  /**
   * Updates the description and caption of a UserMedia document
   * @param mediaId The ID of the media document to update
   * @param updates Object containing the description and/or caption to update
   * @returns An Observable that completes when the update is successful
   */
  updateMediaDetails(mediaId: string, updates: { description?: string; caption?: string }): Observable<void> {
    // Use userMediaService.updateMedia API
    return this.userMediaService.updateMedia(mediaId, updates).pipe(
      map(() => void 0),  // Transform to void to maintain same return type
      catchError(error => {
        console.error('Error updating media details:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a media item from both MongoDB and Firebase Storage
   * @param media The UserMedia item to delete
   * @returns An Observable that completes when deletion is successful
   * 
   * @remarks
   * This method performs a complete deletion:
   * 1. Deletes all 4 image sizes from Firebase Storage (large, medium, small, mobile)
   * 2. Soft deletes the document from MongoDB via API
   * Both operations must succeed for the deletion to be considered complete.
   */
  deleteMedia(media: UserMedia): Observable<void> {
    // console.log('🔍 Storage instance bucket:', this.storage.app.options.storageBucket);
    // console.log('🔍 Media URLs:', {
    //   large: media.urls.large,
    //   medium: media.urls.medium,
    //   small: media.urls.small,
    //   mobile: media.urls.mobile
    // });

    // Extract the file paths from Firebase Storage URLs
    const extractPath = (url: string): string => {
      try {
        // console.log('Extracting path from URL:', url);
        
        const urlObj = new URL(url);
        // console.log('URL hostname:', urlObj.hostname);
        // console.log('URL pathname:', urlObj.pathname);
        
        // Firebase Storage URLs format: /v0/b/{bucket}/o/{path}?alt=media&token={token}
        // Extract bucket from URL to verify it matches
        const bucketMatch = urlObj.pathname.match(/\/b\/([^\/]+)\/o\//);
        if (bucketMatch) {
          // console.log('URL bucket:', bucketMatch[1]);
          // console.log('Expected bucket:', this.storage.app.options.storageBucket);
          
          if (bucketMatch[1] !== this.storage.app.options.storageBucket) {
            // console.error('❌ BUCKET MISMATCH! URL bucket does not match storage instance bucket');
          }
        }
        
        // The path after /o/ is URL-encoded
        const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);
        
        if (pathMatch && pathMatch[1]) {
          // Decode the URL-encoded path (e.g., usermedias%2Fuser123%2Forg456%2Ffile.jpg)
          const decodedPath = decodeURIComponent(pathMatch[1]);
          // console.log('Extracted path:', decodedPath);
          
          // Validate path is not empty or root
          if (decodedPath && decodedPath.trim() !== '' && decodedPath !== '/') {
            return decodedPath;
          }
        }
        
        // console.warn('Could not extract valid path from URL');
        return '';
      } catch (error) {
        // console.error('Failed to extract path from URL:', url, error);
        return '';
      }
    };

    const largePath = extractPath(media.urls.large);
    const mediumPath = extractPath(media.urls.medium);
    const smallPath = extractPath(media.urls.small);
    const mobilePath = extractPath(media.urls.mobile);

    // Log paths for debugging
    // console.log('📁 Media file paths to delete:', {
    //   large: largePath,
    //   medium: mediumPath,
    //   small: smallPath,
    //   mobile: mobilePath
    // });

    // Create delete observables only for valid paths
    const deleteOperations = [
      { path: largePath, name: 'large', url: media.urls.large },
      { path: mediumPath, name: 'medium', url: media.urls.medium },
      { path: smallPath, name: 'small', url: media.urls.small },
      { path: mobilePath, name: 'mobile', url: media.urls.mobile }
    ]
      .filter(item => {
        const isValid = item.path && item.path.trim() !== '';
        if (!isValid) {
          console.warn(`⚠️ Skipping ${item.name} - invalid path`);
        }
        return isValid;
      })
      .map(item => {
        // console.log(`🗑️ Creating storage ref for ${item.name}:`, item.path);
        const storageRef = ref(this.storage, item.path);
        // console.log(`📍 Storage ref created - bucket: ${storageRef.bucket}, fullPath: ${storageRef.fullPath}, name: ${storageRef.name}`);
        
        // Maintain injection context by calling Firebase functions in the Observable chain
        // First verify the file exists
        return of(storageRef).pipe(
          switchMap(sRef => from(getMetadata(sRef)).pipe(
            tap(metadata => {
              // console.log(`📄 File exists - ${item.name}:`, {
              //   name: metadata.name,
              //   size: metadata.size,
              //   contentType: metadata.contentType,
              //   timeCreated: metadata.timeCreated
              // });
            }),
            map(() => sRef)
          )),
          switchMap(sRef => {
            // console.log(`🗑️ Now deleting ${item.name}...`);
            return from(deleteObject(sRef)).pipe(map(() => sRef));
          }),
          switchMap(sRef => {
            // console.log(`✅ Delete command completed for ${item.name}, verifying deletion...`);
            // Verify the file is actually gone
            return from(getMetadata(sRef)).pipe(
              map(() => {
                // console.error(`❌ VERIFICATION FAILED: ${item.name} still exists after deletion!`);
                return false;
              }),
              catchError(verifyError => {
                if (verifyError.code === 'storage/object-not-found') {
                  // console.log(`✅ VERIFIED: ${item.name} successfully deleted (file not found)`);
                  return of(true);
                } else {
                  // console.error(`⚠️ Verification error for ${item.name}:`, verifyError);
                  return of(false);
                }
              })
            );
          }),
          catchError(error => {
            if (error.code === 'storage/object-not-found') {
              // console.warn(`⚠️ ${item.name} not found (may have been deleted already)`);
              return of(true);
            }
            // console.error(`❌ Failed to delete ${item.name} image at path: ${item.path}`);
            // console.error(`Error code: ${error.code}, message: ${error.message}`);
            // console.error('Full error:', error);
            // Don't swallow the error - rethrow it
            return throwError(() => error);
          })
        );
      });

    // console.log(`📊 Total delete operations to perform: ${deleteOperations.length}`);

    // If no valid paths, just delete from MongoDB
    const storageDeletes = deleteOperations.length > 0 
      ? forkJoin(deleteOperations)
      : from([[]]);

    // Delete from Firebase Storage first, then MongoDB
    return storageDeletes.pipe(
      map(() => {
        // console.log('✅ All Firebase Storage deletions completed');
      }),
      switchMap(() => {
        // console.log('🗄️ Deleting from MongoDB...');
        return this.userMediaService.deleteMedia(media._id);
      }),
      map(() => {
        // console.log('✅ Successfully deleted from MongoDB');
        return void 0;
      }),
      catchError(error => {
        // console.error('❌ Error during media deletion:', error);
        return throwError(() => error);
      })
    );
  }

}